/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Dialog } from '@app/cluster-management/constants';
import { DIALOG_UPDATE_CLUSTER_LICENSE_ID } from '@app/cluster-management/dialog-update-cluster-license/constants';
import { Confirm } from '@app/common/modules/gmc-confirm/confirm.service';
import { waitForAllDistributedJobsToFinish } from '@app/common/rxjs-operators/completed-cluster-action';
import { extractError, isTruthy } from '@app/common/utils';
import {
  adminToggleClusterFeatures,
  adminToggleClusterFeaturesErr,
  adminToggleClusterFeaturesSucc,
  changeTdeMasterKey,
  changeTdeMasterKeyErr,
  changeTdeMasterKeyOk,
  confirmClusterActivation,
  confirmClusterDeactivation,
  downloadLogs,
  downloadLogsErr,
  exportLogs,
  exportLogsErr,
  exportLogsOk,
  getAgentVersionLinksError,
  getClusterInfoError,
  getNodeConfigurationErr,
  getThreadDumpErr,
  updateClusterLicenseAction,
  updateClusterLicenseErr,
  updateClusterLicenseOk,
} from '@app/core/ngrx/actions';
import { NodeApi } from '@app/core/services';
import { Clusters } from '@app/core/services/clusters.service';
import { ConfigurationApiService } from '@app/core/services/configuration-api.service';
import { RouteConnectionToken } from '@app/core/services/route-connection-token.service';
import { WebSocketConnection } from '@app/core/services/websocket-connection.service';
import { AppState } from '@app/core/types';
import { getPersonalTeam } from '@app/domain/teams';
import { handleLoading } from '@common/rxjs-operators';
import { interceptActionTaskErrors } from '@common/utils/task-interceptor';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction, routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { isCluster2x } from '@shared/domain/cluster';
import { ClusterInfo, ClusterInfo2x, ToggleClusterFeature, ToggleClusterFeaturesBody } from '@shared/types/cluster';
import { ErrorJob, ResponseStatus } from '@shared/types/cluster-actions';
import { GmcStompErrorCode } from '@shared/types/common';
import {
  EMPTY,
  catchError,
  delay,
  filter,
  map,
  merge,
  mergeMap,
  of,
  pluck,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs';
import {
  activateCluster,
  activateClusterErr,
  activateClusterOk,
  attachCluster,
  attachClusterError,
  attachClusterOk,
  clusterTopology,
  clusters as clustersAction,
  currentClusterChange,
  deactivateCluster,
  deactivateClusterErr,
  deactivateClusterOk,
  detachCluster,
  detachClusterErr,
  detachClusterIntent,
  detachClusterOk,
  login,
  logoutSuccess,
} from './actions';
import {
  clusterById,
  clusterList,
  clustersLoaded,
  currentCluster,
  routeClusterId,
  routerState,
  targetSelector,
  teams,
} from './app.selectors';
const INVALID_CLUSTER_ID_CODES = new Set<GmcStompErrorCode>([
  'err.account-have-not-attached-cluster',
  'err.account-not-auth-for-subscribe-on-topic',
  'err.incorrect-cluster-id',
]);

@Injectable()
export class ClusterEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private confirm: Confirm,
    private clusters: Clusters,
    private ws: WebSocketConnection,
    private connectionToken: RouteConnectionToken,
    private configApi: ConfigurationApiService,
    private nodeApi: NodeApi,
    private matDialog: MatDialog,
  ) {}

  initLoading$ = createEffect(() =>
    this.actions$.pipe(
      handleLoading([
        { start: attachCluster, end: [attachClusterOk, attachClusterError] },
        { start: activateCluster, end: [activateClusterOk, activateClusterErr] },
        { start: deactivateCluster, end: [deactivateClusterOk, deactivateClusterErr] },
      ]),
    ),
  );

  changeCurrentClusterByNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction): string | undefined | null =>
          action?.payload?.routerState?.root?.firstChild?.firstChild?.params.clusterId,
      ),
      filter((newId): newId is string => !!newId),
      switchMap((newId) =>
        this.store.select(clustersLoaded).pipe(
          filter((loaded) => !!loaded),
          take(1),
          withLatestFrom(this.store.select(currentCluster)),
          pluck(1),
          filter((currentId) => newId !== currentId),
          withLatestFrom(this.store.select(clusterList)),
          map(([currentId, list]) => currentClusterChange({ id: list.some((el) => el.id === newId) ? newId : null })),
        ),
      ),
    ),
  );

  syncClusterChangeToLocation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(currentClusterChange),
        withLatestFrom(this.store.select(routeClusterId), this.store.select(routerState)),
        tap(([{ id }, routeClusterId, routerState]) => {
          if (!id || id === routeClusterId || !routeClusterId) return;
          this.router.navigateByUrl(routerState.state.url.replace(routeClusterId, id));
        }),
      ),
    { dispatch: false },
  );

  attachCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(attachCluster),
      switchMap((a) =>
        this.clusters.attach(a.connectionToken, a.clusterCredentials).pipe(
          map((cluster) => attachClusterOk({ cluster })),
          catchError((e) => of(attachClusterError({ error: extractError(e), silent: true }))),
        ),
      ),
    ),
  );

  redirectOnClusterAddSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(attachClusterOk),
        tap(() => this.matDialog.getDialogById(Dialog.ATTACH_CLUSTER_ID)?.close()),
        switchMap((action) =>
          this.actions$.pipe(
            // Wait until "Add Cluster" dialog is closed, otherwise navigation won't work
            ofType(routerNavigatedAction),
            take(1),
            tap(() => {
              this.router.navigateByUrl(`/clusters/${action.cluster.id}/monitoring-dashboard`);
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  confirmActivateCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmClusterActivation),
      pluck('clusterId'),
      switchMap((clusterId) =>
        this.store.select(clusterById(clusterId)).pipe(
          take(1),
          switchMap((cluster) =>
            !cluster
              ? EMPTY
              : this.confirm
                  .confirm({
                    title: `Activate the cluster?`,
                    message: `This action will activate cluster "${cluster.tag || cluster.id}"\nNote: rebalance could be triggered.`,
                    dismissButtonLabel: `Cancel`,
                    confirmButtonLabel: `Activate`,
                  })
                  .pipe(switchMap((value) => (value ? of(activateCluster({ clusterId })) : EMPTY))),
          ),
        ),
      ),
    ),
  );

  activateCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateCluster),
      mergeMap((a) =>
        this.clusters.activate(a.clusterId).pipe(
          map(() =>
            activateClusterOk({
              notification: `Cluster activated`,
            }),
          ),
          catchError((err) => of(activateClusterErr({ error: extractError(err), clusterId: a.clusterId }))),
        ),
      ),
    ),
  );

  confirmDeactivateCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmClusterDeactivation),
      pluck('clusterId'),
      switchMap((clusterId) =>
        this.store.select(clusterById(clusterId)).pipe(
          take(1),
          switchMap((cluster) =>
            !cluster
              ? EMPTY
              : this.confirm
                  .confirm({
                    title: `Deactivate the cluster?`,
                    message: `This action will deactivate cluster "${cluster.tag || cluster.id}".\nNote: this action may cancel the running tasks. Any manipulations with caches are not available while the cluster is inactive.`,
                    dismissButtonLabel: `Cancel`,
                    confirmButtonLabel: `Deactivate`,
                  })
                  .pipe(switchMap((value) => (value ? of(deactivateCluster({ clusterId })) : EMPTY))),
          ),
        ),
      ),
    ),
  );

  deactivateCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deactivateCluster),
      mergeMap((a) =>
        this.clusters.deactivate(a.clusterId).pipe(
          map(() =>
            deactivateClusterOk({
              notification: `Cluster deactivated`,
            }),
          ),
          catchError((err) => of(deactivateClusterErr({ error: extractError(err), clusterId: a.clusterId }))),
        ),
      ),
    ),
  );

  changeTdeMasterKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeTdeMasterKey),
      mergeMap((a) =>
        this.clusters.changeTdeMasterKey(a.clusterId, a.masterKey).pipe(
          map(() =>
            changeTdeMasterKeyOk({
              notification: 'The AWS key successfully updated',
            }),
          ),
          catchError((err) => of(changeTdeMasterKeyErr({ error: extractError(err), clusterId: a.clusterId }))),
        ),
      ),
    ),
  );

  getNodeConfigurationError$ = createEffect(() =>
    interceptActionTaskErrors(this.nodeApi, 'getNodeConfiguration').pipe(
      map((error) =>
        getNodeConfigurationErr({
          error: extractError(error, 'Failed to get node configuration'),
        }),
      ),
    ),
  );

  getThreadDumpError$ = createEffect(() =>
    interceptActionTaskErrors(this.nodeApi, 'getThreadDump').pipe(
      map((error) =>
        getThreadDumpErr({
          error: extractError(error, 'Failed to get thread dump'),
        }),
      ),
    ),
  );

  confirmClusterDetach$ = createEffect(() =>
    this.actions$.pipe(
      ofType(detachClusterIntent),
      withLatestFrom(this.store.select(targetSelector).pipe(filter(isTruthy))),
      switchMap(([{ cluster }, target]) =>
        this.confirm
          .confirm({
            title: `Detach cluster “${cluster.tag}”?`,
            message:
              target === 'hosted'
                ? 'The cluster will be disconnected. It will not be billed, and all current users and teams will lose access to it.'
                : 'The cluster will be disconnected. All current users and teams will lose access to it.',
            dismissButtonLabel: 'Cancel',
            confirmButtonLabel: 'Detach',
          })
          .pipe(switchMap((value) => (value ? of(detachCluster({ cluster })) : EMPTY))),
      ),
    ),
  );

  detachCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(detachCluster),
      withLatestFrom(this.store.select(teams).pipe(map((teams) => (teams ? getPersonalTeam(teams) : null)))),
      mergeMap(([{ cluster }, personalTeam]) =>
        personalTeam
          ? this.clusters.removeClusterFromTeam(cluster.id, personalTeam.id).pipe(
              map(() =>
                detachClusterOk({
                  notification: `Cluster “${cluster.tag}” detached`,
                }),
              ),
              catchError((err) => of(detachClusterErr({ error: extractError(err) }))),
            )
          : EMPTY,
      ),
    ),
  );

  clusterTopology$ = createEffect(() =>
    this.store.select(clusterList).pipe(
      // Can't use filter here because switchMap ensures we unsubscribe when there's no cluster ID
      switchMap((clusters) =>
        merge(
          ...clusters
            .filter((cluster) => isCluster2x(cluster))
            .map((cluster) =>
              this.clusters
                .clusterTopology(cluster.id)
                .pipe(map((data) => clusterTopology({ data, clusterId: cluster.id }))),
            ),
        ),
      ),
    ),
  );

  invalidClusterIdRedirect$ = createEffect(
    () =>
      this.ws.errors$.pipe(
        filter(({ error }) => error.code !== null && INVALID_CLUSTER_ID_CODES.has(error.code)),
        tap(() => this.router.navigateByUrl('/clusters')),
      ),
    { dispatch: false },
  );

  clusters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      switchMap(() =>
        this.ws.getTopic<ClusterInfo[]>('/user/topic/cluster/list').pipe(
          map((data) => clustersAction({ data })),
          takeUntil(this.actions$.pipe(ofType(logoutSuccess))),
        ),
      ),
    ),
  );

  consumeUsedConnectionToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        tap(() => this.connectionToken.consumeToken()),
      ),
    { dispatch: false },
  );

  getAgentVersionLinksError$ = createEffect(() =>
    interceptActionTaskErrors(this.configApi, 'getAgentVersionLinks').pipe(
      map((error) =>
        getAgentVersionLinksError({
          error: extractError(error, 'Failed to get versions'),
        }),
      ),
    ),
  );

  getClusterInfoError$ = createEffect(() =>
    interceptActionTaskErrors(this.clusters, 'getClusterByToken').pipe(
      map((error) =>
        getClusterInfoError({
          error: extractError(error, 'Failed to get a cluster by provided connection token'),
          silent: true,
        }),
      ),
    ),
  );

  private getFeatureToggleSettings = (
    cluster: ClusterInfo2x,
    feature: ToggleClusterFeature,
    value: boolean,
  ): ToggleClusterFeaturesBody => {
    const currentValue: { [key in ToggleClusterFeature]: { enabled: boolean } } = {
      compute: {
        enabled: cluster.info.configuration.computeEnabled,
      },
      tracing: {
        enabled: cluster.info.configuration.tracingEnabled,
      },
      metrics: {
        enabled: cluster.info.configuration.metricsEnabled,
      },
      runningQueries: {
        enabled: cluster.info.configuration.runningQueriesEnabled,
      },
    };
    currentValue[feature].enabled = value;
    return currentValue;
  };

  adminFeatureToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminToggleClusterFeatures),
      switchMap((event) => {
        return this.store.select(clusterById(event.clusterId)).pipe(
          take(1),
          switchMap((cluster) =>
            cluster && isCluster2x(cluster)
              ? this.clusters
                  .toggleClusterFeatures(
                    cluster.id,
                    this.getFeatureToggleSettings(cluster, event.feature, event.enabled),
                  )
                  .pipe(
                    map(() => {
                      const features: { [key in ToggleClusterFeature]: string } = {
                        metrics: 'Metric monitoring',
                        compute: 'Compute monitoring',
                        tracing: 'Tracing',
                        runningQueries: 'Running queries',
                      };
                      const currentFeature = features[event.feature];
                      const action = event.enabled ? 'enabled' : 'disabled';
                      return adminToggleClusterFeaturesSucc({
                        notification: `${currentFeature} ${action} for cluster "${cluster.tag}"`,
                      });
                    }),
                    catchError((err) =>
                      of(
                        adminToggleClusterFeaturesErr({
                          clusterId: event.clusterId,
                          feature: event.feature,
                          enabled: event.enabled,
                          error: extractError(err, 'Failed to toggle cluster feature'),
                        }),
                      ),
                    ),
                  )
              : EMPTY,
          ),
        );
      }),
    ),
  );

  exportLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exportLogs),
      delay(1_000), // to animate progress
      switchMap(({ clusterId, date }) =>
        this.clusters.exportLogs(clusterId, date).pipe(
          map((it) =>
            exportLogsOk({
              clusterId,
              resourceId: it.result.resourceId,
              date,
            }),
          ),
          catchError((err) => of(exportLogsErr({ error: extractError(err), clusterId, silent: true }))),
        ),
      ),
    ),
  );

  downloadExportedLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exportLogsOk, downloadLogs),
      switchMap(({ resourceId, date }) =>
        this.clusters.downloadExportedLogs(resourceId, date).pipe(
          switchMap(() => EMPTY),
          catchError((err) =>
            of(
              downloadLogsErr({
                error: extractError(err, 'Failed to download logs'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Update cluster license

  updateLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateClusterLicenseAction),
      switchMap((action) => {
        const handleErrors = (errorJobs: ErrorJob[]) => {
          const firstJob = errorJobs[0];
          return errorJobs.length && firstJob
            ? errorJobs.length === 1
              ? of(
                  updateClusterLicenseErr({
                    clusterId: action.clusterId,
                    error: extractError(
                      errorJobs[0],
                      `Failed to update cluster license on node with consistent ID: ${firstJob.nodeConsistentId}`,
                    ),
                    silent: false,
                  }),
                )
              : of(
                  ...errorJobs.map((job) =>
                    updateClusterLicenseErr({ clusterId: action.clusterId, error: extractError(job), silent: true }),
                  ),
                  updateClusterLicenseErr({
                    clusterId: action.clusterId,
                    error: extractError(`Failed to update cluster license`),
                  }),
                )
            : of(updateClusterLicenseOk({ clusterId: action.clusterId, notification: 'Cluster license updated' }));
        };
        return this.clusters.updateLicense(action.clusterId, action.license).pipe(
          waitForAllDistributedJobsToFinish,
          filter((res) => res.job.status !== ResponseStatus.RUNNING),
          toArray(),
          map((responses) => responses.map((res) => res.job)),
          switchMap((jobs) => {
            const errorJobs = jobs.filter((job): job is ErrorJob => job.status === ResponseStatus.FAILED);
            return handleErrors(errorJobs);
          }),
          catchError(handleErrors),
        );
      }),
    ),
  );

  closeDialogOnLicenseUpdateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateClusterLicenseOk),
        tap(() => this.matDialog.getDialogById(DIALOG_UPDATE_CLUSTER_LICENSE_ID)?.close()),
      ),
    { dispatch: false },
  );
}
